// Choosing a model is one decision, and this dialog exists to make it in a second: the models
// you used last, then every provider's list, by name, with search. Everything that is worth
// reading about a model — ids, descriptions, capabilities, the full parameter list, pricing —
// lives in the Model Garden at /models (§19).
//
// That split is also the fix for the bug that prompted it. The old picker rendered a
// deprecation note inside a .chip, and chips are white-space: nowrap, so a sentence forced the
// dialog wider than the viewport and the whole page scrolled sideways. Nothing in here holds a
// sentence except a one-clause reason, and that is plain text.

import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import type { Catalog, GenerationMode, Model, Provider } from "../api";
import { Link } from "react-router-dom";
import { Modal } from "./ui";
import { RECENT_MODELS_SHOWN, usePreferences } from "../hooks/usePreferences";

interface Entry {
  provider: Provider;
  model: Model;
}

function findEntry(catalog: Catalog, modelId: string): Entry | undefined {
  for (const provider of catalog.providers) {
    const model = provider.models.find((candidate) => candidate.id === modelId);
    if (model) return { provider, model };
  }
  return undefined;
}

/** Why this model cannot be chosen right now. One clause, or null when it can. */
function blockedReason(provider: Provider, model: Model, mode: GenerationMode): string | null {
  if (!provider.configured) return `${provider.display_name} has no API key`;
  if (mode === "edit" && !model.capabilities.supports_edit) return "Cannot edit an image";
  if (mode === "generate" && !model.capabilities.supports_generate) return "Needs a reference image";
  return null;
}

// The id is not shown, but it still matches: someone who knows a model by its id should be able
// to type it.
function matches(provider: Provider, model: Model, search: string): boolean {
  if (search === "") return true;
  const haystack = `${model.display_name} ${model.id} ${provider.display_name}`.toLowerCase();
  return haystack.includes(search);
}

interface ModelPickerProps {
  catalog: Catalog;
  selectedModelId: string;
  mode: GenerationMode;
  onSelect: (modelId: string) => void;
}

export function ModelPicker({
  catalog,
  selectedModelId,
  mode,
  onSelect,
}: ModelPickerProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [preferences] = usePreferences();
  const selected = findEntry(catalog, selectedModelId)?.model;

  const needle = search.trim().toLowerCase();

  // Most recent first, as stored. A remembered model that has since left the catalog is skipped
  // rather than shown as a dead row.
  const recent = useMemo(() => {
    const entries: Entry[] = [];
    for (const modelId of preferences.recent_model_ids) {
      const entry = findEntry(catalog, modelId);
      if (entry && matches(entry.provider, entry.model, needle)) entries.push(entry);
      if (entries.length === RECENT_MODELS_SHOWN) break;
    }
    return entries;
  }, [catalog, preferences.recent_model_ids, needle]);

  const groups = useMemo(
    () =>
      catalog.providers
        .map((provider) => ({
          provider,
          models: provider.models.filter((model) => matches(provider, model, needle)),
        }))
        .filter((group) => group.models.length > 0),
    [catalog, needle],
  );

  function choose(modelId: string): void {
    onSelect(modelId);
    setOpen(false);
  }

  function renderRow({ provider, model }: Entry, showProvider: boolean): ReactElement {
    const blocked = blockedReason(provider, model, mode);
    const current = model.id === selectedModelId;

    return (
      <button
        key={model.id}
        type="button"
        className={current ? "list-row is-selected" : "list-row"}
        disabled={blocked !== null}
        aria-current={current ? "true" : undefined}
        onClick={() => choose(model.id)}
      >
        <span className="col grow">
          <span>{model.display_name}</span>
          {showProvider && <span className="stamp">{provider.display_name}</span>}
          {blocked && <span className="stamp">{blocked}</span>}
        </span>
        {current && <Check size={16} aria-hidden />}
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className="btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span className="truncate">{selected ? selected.display_name : "Choose a model"}</span>
        <ChevronDown size={14} aria-hidden />
      </button>

      {open && (
        <Modal
          title="Choose a model"
          onClose={() => setOpen(false)}
          footer={
            <Link className="btn ghost" to="/models" onClick={() => setOpen(false)}>
              Compare all models →
            </Link>
          }
        >
          <div className="search">
            <Search size={15} aria-hidden />
            <input
              className="input"
              type="search"
              value={search}
              placeholder="Search models"
              aria-label="Search models"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          {groups.length === 0 && <p className="field-help">No model matches that search.</p>}

          {recent.length > 0 && (
            <section>
              <h3 className="list-group-title">Recently used</h3>
              {/* Outside its provider group, a name alone can be ambiguous, so the provider rides along. */}
              <div className="list">{recent.map((entry) => renderRow(entry, true))}</div>
            </section>
          )}

          {groups.map(({ provider, models }) => (
            <section key={provider.id}>
              <h3 className="list-group-title">{provider.display_name}</h3>
              <div className="list">
                {models.map((model) => renderRow({ provider, model }, false))}
              </div>
            </section>
          ))}
        </Modal>
      )}
    </>
  );
}
